/// <reference path="../../../types/axios-fix.d.ts" />
import { BaseService, httpClientService, authInterceptorService, errorHandlerService, cacheService } from '../core';

/**
 * User interface - matching the monolith's User type
 */
export interface User {
  _id: string;
  id?: string;  // Optional alias for _id (some API responses use 'id')
  email: string;
  name?: string;
  isEmailVerified: boolean;
  twoFactorEnabled: boolean;
  verified_2fa_methods?: string[]; // List of verified 2FA methods: 'email', 'sms', 'totp'
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  preferences?: any;
  avatarUrl?: string;
  phoneNumber?: string;
  bio?: string;
}

/**
 * Auth response interface
 */
export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

/**
 * Two-factor response
 */
export interface TwoFactorResponse {
  requiresTwoFactor: boolean;
  userId: string;
  user?: User | null; // Include user data for 2FA method selection
}

/**
 * OAuth Provider type
 */
export type OAuthProvider = 'google' | 'microsoft' | 'github' | 'facebook';

/**
 * Auth Service
 * 
 * Handles authentication operations including:
 * - User registration
 * - Login with email/password
 * - Two-factor authentication
 * - OAuth authentication
 * - Token management
 * - Password reset
 * 
 * Note: Most token management is handled by AuthInterceptorService.
 * This service focuses on the auth operations themselves.
 * 
 * Extracted from the monolithic API service.
 */
export class AuthService extends BaseService {
  constructor() {
    super(httpClientService, authInterceptorService, errorHandlerService, cacheService);
  }

  /**
   * Normalize user data from various API response formats
   */
  private normalizeUser(userData: any): User {
    return {
      _id: userData._id || userData.id || '',
      email: userData.email || '',
      name: userData.name || '',
      isEmailVerified: userData.isEmailVerified || userData.is_email_verified || userData.emailVerified || false,
      twoFactorEnabled: userData.twoFactorEnabled || userData.two_factor_enabled || false,
      verified_2fa_methods: userData.verified_2fa_methods || userData.verified2faMethods || [],
      isActive: userData.isActive !== undefined ? userData.isActive : true,
      lastLoginAt: userData.lastLoginAt || userData.last_login_at || userData.last_login,
      createdAt: userData.createdAt || userData.created_at || '',
      updatedAt: userData.updatedAt || userData.updated_at || '',
      preferences: userData.preferences || {},
      avatarUrl: userData.avatarUrl || userData.avatar_url,
      phoneNumber: userData.phoneNumber || userData.phone_number,
      bio: userData.bio
    };
  }

  /**
   * Register a new user
   */
  async register(email: string, password: string, name?: string, dateOfBirth?: string): Promise<AuthResponse> {
    try {
      const response = await this.post<any>('/auth/register', {
        email,
        password,
        name,
        dateOfBirth // COPPA compliance - ISO date string
      });
      
      // Check if this is an error response
      if (response?.error) {
        throw new Error(response.error.message || 'Registration failed');
      }
      
      // Handle both camelCase and snake_case token field names with null safety
      const accessToken = response?.access_token || response?.accessToken;
      const refreshToken = response?.refresh_token || response?.refreshToken;
      
      if (response.user && accessToken && refreshToken) {
        // Set tokens using the auth interceptor
        this.authInterceptor.setTokens(accessToken, refreshToken);
        
        // Invalidate user cache on successful registration
        this.invalidateCache('user');
        
        // Normalize the response to match our AuthResponse interface
        return {
          user: this.normalizeUser(response.user),
          accessToken: accessToken,
          refreshToken: refreshToken
        };
      } else {
        throw new Error('Invalid registration response');
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.register');
    }
  }

  /**
   * Login with email and password
   */
  async login(email: string, password: string, twoFactorToken?: string): Promise<AuthResponse | TwoFactorResponse> {
    try {
      const payload: any = {
        email,
        password
      };

      if (twoFactorToken) {
        payload.two_factor_token = twoFactorToken;
      }

      const response = await this.post<any>('/auth/login', payload);
      
      // Check if this is an error response (shouldn't happen if BaseService works correctly, but just in case)
      if (response && response.error) {
        throw new Error(response.error.message || 'Authentication failed');
      }
      
      // Check if two-factor auth is required - handle both field name formats
      if (response && typeof response === 'object' &&
          (('requiresTwoFactor' in response && response.requiresTwoFactor) ||
           ('two_factor_required' in response && response.two_factor_required))) {
        return {
          requiresTwoFactor: true,
          userId: response.userId || response.user_id || response.user?.id,
          user: response.user ? this.normalizeUser(response.user) : null
        };
      }
      
      
      // Normal login success - handle both camelCase and snake_case token field names with null safety
      const accessToken = response?.access_token || response?.accessToken;
      const refreshToken = response?.refresh_token || response?.refreshToken;
      const user = response?.user;
      
      if (user && accessToken && refreshToken) {
        // Set tokens using the auth interceptor
        this.authInterceptor.setTokens(accessToken, refreshToken);
        
        // Invalidate user cache on successful login
        this.invalidateCache('user');
        
        // Normalize the response to match our AuthResponse interface
        return {
          user: this.normalizeUser(user),
          accessToken: accessToken,
          refreshToken: refreshToken
        };
      } else {
        console.error('Missing required fields in login response:', {
          hasUser: !!user,
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          responseData: response
        });
        throw new Error('Invalid authentication response');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.login');
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      // Call logout endpoint
      await this.post('/auth/logout', {});
    } catch (error) {
      // Ignore logout errors - clear tokens anyway
      console.warn('Logout request failed, clearing tokens anyway:', error);
    } finally {
      // Clear tokens and cache
      this.authInterceptor.clearTokens();
      this.cache.clearCache();
    }
  }

  /**
   * Get available OAuth providers
   * Returns list of providers that are configured in the backend
   */
  async getAvailableOAuthProviders(): Promise<OAuthProvider[]> {
    try {
      const response = await this.get<{ providers: string[] }>('/auth/oauth/providers');
      return response.providers as OAuthProvider[];
    } catch (error) {
      console.error('Failed to get available OAuth providers:', error);
      // Return empty array on error to gracefully hide OAuth buttons
      return [];
    }
  }

  /**
   * Get OAuth authorization URL
   */
  async getOAuthUrl(provider: OAuthProvider): Promise<{ authUrl: string }> {
    const redirectUri = `${window.location.origin}/oauth/callback`;
    const state = this.generateRandomState();

    // Store state in sessionStorage for verification
    sessionStorage.setItem('oauth_state', state);

    try {
      const response = await this.get<{ authUrl: string }>(
        `/auth/oauth/${provider}/authorize`,
        {
          params: {
            redirect_uri: redirectUri,
            state: state
          }
        }
      );

      return response;
    } catch (error) {
      console.error(`OAuth URL generation error for ${provider}:`, error);
      throw this.errorHandler.handleError(error, `AuthService.getOAuthUrl(${provider})`);
    }
  }

  /**
   * Verify two-factor authentication token
   */
  async verifyTwoFactor(userId: string, token: string): Promise<AuthResponse> {
    try {
      const response = await this.post<any>('/auth/verify-2fa', {
        userId: userId,
        token: token
      });
      
      // Check if this is an error response
      if (response?.error) {
        throw new Error(response.error.message || 'Two-factor verification failed');
      }
      
      // Handle both camelCase and snake_case token field names with null safety
      const accessToken = response?.access_token || response?.accessToken;
      const refreshToken = response?.refresh_token || response?.refreshToken;
      const user = response?.user;
      
      if (user && accessToken && refreshToken) {
        // Set tokens using the auth interceptor
        this.authInterceptor.setTokens(accessToken, refreshToken);
        
        // Invalidate user cache
        this.invalidateCache('user');
        
        return {
          user: this.normalizeUser(user),
          accessToken: accessToken,
          refreshToken: refreshToken
        };
      } else {
        throw new Error('Invalid two-factor verification response');
      }
    } catch (error) {
      console.error('Two-factor verification error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.verifyTwoFactor');
    }
  }

  /**
   * Setup two-factor authentication
   */
  async setupTwoFactor(): Promise<{
    secret: string;
    qrCodeUrl: string;
    backupCodes: string[];
  }> {
    try {
      const response = await this.post<any>('/auth/setup-2fa', {});
      
      return {
        secret: response.secret,
        qrCodeUrl: response.qrCodeUrl || response.qr_code_url,
        backupCodes: response.backupCodes || response.backup_codes || []
      };
    } catch (error) {
      console.error('Two-factor setup error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.setupTwoFactor');
    }
  }

  /**
   * Enable two-factor authentication simply (no TOTP required)
   * Requires only verified email/SMS
   */
  async enableTwoFactorSimple(): Promise<{ user: User; success: boolean }> {
    try {
      const response = await this.post<any>('/auth/enable-2fa-simple', {});

      // Invalidate user cache
      this.invalidateCache('user');

      return {
        user: this.normalizeUser(response.user),
        success: true
      };
    } catch (error) {
      console.error('Enable two-factor simple error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.enableTwoFactorSimple');
    }
  }

  /**
   * Enable two-factor authentication with TOTP
   */
  async enableTwoFactor(token: string): Promise<{ user: User; success: boolean }> {
    try {
      const response = await this.post<any>('/auth/enable-2fa', { confirmation_code: token });

      // Invalidate user cache
      this.invalidateCache('user');

      return {
        user: this.normalizeUser(response.user),
        success: true
      };
    } catch (error) {
      console.error('Enable two-factor error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.enableTwoFactor');
    }
  }

  /**
   * Disable two-factor authentication
   */
  async disableTwoFactor(
    password?: string,
    otpCode?: string,
    totpToken?: string
  ): Promise<{ user: User; success: boolean }> {
    try {
      const payload: any = {};
      if (password) payload.current_password = password;
      if (otpCode) payload.otp_code = otpCode;
      if (totpToken) payload.totp_token = totpToken;

      const response = await this.post<any>('/auth/disable-2fa', payload);

      // Invalidate user cache
      this.invalidateCache('user');

      return {
        user: this.normalizeUser(response.user),
        success: true
      };
    } catch (error) {
      console.error('Disable two-factor error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.disableTwoFactor');
    }
  }

  /**
   * Disable TOTP (authenticator app) while keeping other 2FA methods
   */
  async disableTotp(
    password?: string,
    otpCode?: string,
    totpToken?: string
  ): Promise<{ user: User; success: boolean; message: string }> {
    try {
      const payload: any = {};
      if (password) payload.password = password;
      if (otpCode) payload.otp_code = otpCode;
      if (totpToken) payload.totp_token = totpToken;

      const response = await this.post<any>('/auth/disable-totp', payload);

      // Invalidate user cache
      this.invalidateCache('user');

      return {
        user: this.normalizeUser(response.user),
        success: response.success,
        message: response.message || 'TOTP disabled successfully'
      };
    } catch (error) {
      console.error('Disable TOTP error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.disableTotp');
    }
  }

  /**
   * Send two-factor OTP code via email or SMS (requires authentication)
   * Use this when already logged in (e.g., testing 2FA from profile page)
   */
  async sendTwoFactorOtp(channel: 'email' | 'sms'): Promise<{
    success: boolean;
    message: string;
    expiresAt?: string;
  }> {
    try {
      const response = await this.post<any>('/auth/2fa/otp/send', { channel });

      return {
        success: response.success || true,
        message: response.message || `Verification code sent to your ${channel}`,
        expiresAt: response.expiresAt || response.expires_at
      };
    } catch (error) {
      console.error('Send two-factor OTP error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.sendTwoFactorOtp');
    }
  }

  /**
   * Send OTP during login (NO authentication required)
   * Use this during the login flow when user doesn't have a token yet
   * Verifies email/password before sending the OTP code
   */
  async sendLoginOtp(email: string, password: string, channel: 'email' | 'sms'): Promise<{
    success: boolean;
    message: string;
    expiresAt?: string;
  }> {
    try {
      // This endpoint doesn't require authentication - it verifies credentials first
      const response = await this.post<any>('/auth/login/send-otp', {
        email,
        password,
        channel
      });

      return {
        success: response.success || true,
        message: response.message || `Verification code sent to your ${channel}`,
        expiresAt: response.expiresAt || response.expires_at
      };
    } catch (error) {
      console.error('Send login OTP error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.sendLoginOtp');
    }
  }

  /**
   * Verify OTP code during login (unauthenticated endpoint)
   */
  async verifyLoginOtp(email: string, password: string, code: string, channel: 'email' | 'sms'): Promise<AuthResponse> {
    try {
      // This endpoint doesn't require authentication - it verifies credentials and OTP together
      const response = await this.post<any>('/auth/login/verify-otp', {
        email,
        password,
        code,
        channel
      });

      // Handle both camelCase and snake_case token field names
      const accessToken = response?.access_token || response?.accessToken;
      const refreshToken = response?.refresh_token || response?.refreshToken;
      const user = response?.user;

      if (user && accessToken && refreshToken) {
        // Set tokens using the auth interceptor (same as login method)
        this.authInterceptor.setTokens(accessToken, refreshToken);

        // Invalidate user cache
        this.invalidateCache('user');

        // Return normalized response (same as login method)
        return {
          user: this.normalizeUser(user),
          accessToken: accessToken,
          refreshToken: refreshToken
        };
      } else {
        console.error('Missing required fields in verify OTP response:', {
          hasUser: !!user,
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          responseData: response
        });
        throw new Error('Invalid authentication response');
      }
    } catch (error) {
      console.error('Verify login OTP error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.verifyLoginOtp');
    }
  }

  /**
   * Verify two-factor OTP code
   */
  async verifyTwoFactorOtp(code: string, channel: 'email' | 'sms'): Promise<{
    valid: boolean;
    message: string;
  }> {
    try {
      const response = await this.post<any>('/auth/2fa/otp/verify', {
        code,
        channel
      });

      return {
        valid: response.valid || false,
        message: response.message || 'Verification completed'
      };
    } catch (error) {
      console.error('Verify two-factor OTP error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.verifyTwoFactorOtp');
    }
  }

  /**
   * Verify email address
   *
   * This endpoint MUST work without authentication because:
   * - Users click the link from their email
   * - The link might open in a different browser where they're not logged in
   * - The token in the URL is the authentication
   */
  async verifyEmail(token: string): Promise<{ user: User; success: boolean }> {
    try {
      // Use request() with X-Skip-Auth header to bypass authentication
      const response = await this.request<any>(
        'POST',
        '/auth/verify-email',
        { token },
        {
          headers: {
            'X-Skip-Auth': 'true'
          }
        }
      );

      // Invalidate user cache
      this.invalidateCache('user');

      return {
        user: response.user ? this.normalizeUser(response.user) : null as any,
        success: true
      };
    } catch (error) {
      console.error('Email verification error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.verifyEmail');
    }
  }

  /**
   * Send verification email
   * Works in both authenticated and unauthenticated contexts:
   * - If authenticated: uses session email (no parameter needed)
   * - If unauthenticated: requires email parameter
   */
  async sendVerificationEmail(email?: string): Promise<{ success: boolean; message: string }> {
    try {
      const payload = email ? { email } : {};
      const response = await this.post<any>('/auth/send-verification', payload);

      return {
        success: response.data?.success || response.success || true,
        message: response.data?.message || response.message || 'Verification email sent'
      };
    } catch (error) {
      console.error('Send verification email error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.sendVerificationEmail');
    }
  }

  /**
   * Request password reset
   */
  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.post<any>('/auth/forgot-password', { email });

      return {
        success: true,
        message: response.message || 'Password reset email sent'
      };
    } catch (error) {
      console.error('Forgot password error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.forgotPassword');
    }
  }

  /**
   * Reset password with token
   * Note: This does NOT log the user in - they need to login with the new password
   */
  async resetPassword(token: string, password: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.post<any>('/auth/reset-password', {
        token,
        newPassword: password
      });

      return {
        success: response.success || true,
        message: response.message || 'Password reset successful'
      };
    } catch (error) {
      console.error('Password reset error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.resetPassword');
    }
  }

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean }> {
    try {
      const response = await this.post<any>('/auth/change-password', {
        currentPassword,
        newPassword
      });

      // Invalidate user cache
      this.invalidateCache('user');

      return {
        success: response.success || true
      };
    } catch (error) {
      console.error('Change password error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.changePassword');
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.authInterceptor.hasValidAuthToken();
  }

  /**
   * Check if user has valid auth token (alias for isAuthenticated)
   */
  hasValidAuthToken(): boolean {
    return this.authInterceptor.hasValidAuthToken();
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return this.authInterceptor.getAccessToken();
  }

  /**
   * Get user profile
   */
  async getProfile(): Promise<User> {
    try {
      const response = await this.get<User>('/users/me');
      return this.normalizeUser(response);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw this.errorHandler.handleError(error, 'AuthService.getProfile');
    }
  }

  /**
   * Clear auth token
   */
  clearAuthToken(): void {
    this.authInterceptor.clearTokens();
  }

  /**
   * Refresh access token
   */
  async refreshAccessToken(): Promise<string | null> {
    try {
      // Get the refresh token
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        console.warn('No refresh token available');
        return null;
      }

      const response = await this.post<any>('/auth/refresh', {
        refresh_token: refreshToken
      });
      
      // Check if this is an error response
      if (response?.error) {
        throw new Error(response.error.message || 'Token refresh failed');
      }
      
      // Handle both camelCase and snake_case field names with null safety
      const accessToken = response?.access_token || response?.accessToken;
      const newRefreshToken = response?.refresh_token || response?.refreshToken;
      
      if (accessToken) {
        // Update tokens
        this.authInterceptor.setTokens(accessToken, newRefreshToken);
        return accessToken;
      }
      return null;
    } catch (error) {
      console.error('Error refreshing access token:', error);
      this.clearAuthToken();
      return null;
    }
  }

  /**
   * Set auth error handler
   */
  setAuthErrorHandler(handler: (() => void) | null): void {
    this.authInterceptor.setAuthErrorHandler(handler);
  }

  /**
   * Set initial password for OAuth-only users
   * Enables dual authentication (password + OAuth)
   */
  async setInitialPassword(newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.post<any>('/auth/set-password', {
        newPassword
      });

      if (response?.error) {
        throw new Error(response.error.message || 'Failed to set password');
      }

      return {
        success: response.success || true,
        message: response.message || 'Password set successfully'
      };
    } catch (error) {
      console.error('Set password error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.setInitialPassword');
    }
  }

  /**
   * Get available authentication methods for current user
   * Returns password status and linked OAuth providers
   */
  async getAuthMethods(): Promise<{
    hasPassword: boolean;
    oauthProviders: string[];
    totalMethods: number;
  }> {
    try {
      const response = await this.get<any>('/auth/methods');

      if (response?.error) {
        throw new Error(response.error.message || 'Failed to get auth methods');
      }

      return {
        hasPassword: response.hasPassword || response.has_password || false,
        oauthProviders: response.oauthProviders || response.oauth_providers || [],
        totalMethods: response.totalMethods || response.total_methods || 0
      };
    } catch (error) {
      console.error('Get auth methods error:', error);
      throw this.errorHandler.handleError(error, 'AuthService.getAuthMethods');
    }
  }

  /**
   * Unlink OAuth provider from account
   * Requires at least one authentication method to remain
   */
  async unlinkOAuthProvider(provider: OAuthProvider): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.delete<any>(`/auth/oauth/${provider}`);

      if (response?.error) {
        throw new Error(response.error.message || `Failed to unlink ${provider}`);
      }

      // Invalidate user cache after unlinking
      this.invalidateCache('user');

      return {
        success: response.success || true,
        message: response.message || `${provider} has been unlinked from your account`
      };
    } catch (error) {
      console.error(`Unlink OAuth provider error for ${provider}:`, error);
      throw this.errorHandler.handleError(error, `AuthService.unlinkOAuthProvider(${provider})`);
    }
  }

  /**
   * Clear authentication (alias for clearAuthToken)
   */
  clearAuth(): void {
    this.authInterceptor.clearTokens();
    this.cache.clearCache();
  }

  /**
   * Generate random state for OAuth
   */
  private generateRandomState(): string {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }
}

// Export singleton instance
export const authService = new AuthService();